import type { Email } from "../lib/jmap";
import { formatTime } from "../lib/settings";
import { Icon } from "./Icon";

/**
 * Row markup is copied exactly from the design's `fmtRow()` in
 * export/syndeo-mail/app.js — .row > .ck / .av / .bd(.l1(.who,.when),.sj,.sn) /
 * .star. mail.css targets those class names, so any deviation silently breaks
 * the layout rather than erroring.
 */

/** Deterministic avatar colour, so a sender keeps the same colour between loads. */
const AVATAR_COLOURS = ["#4F46E5", "#0EA5E9", "#059669", "#B45309", "#DC2626", "#7C3AED", "#DB2777"];
function colourFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length];
}

function initials(email: Email): string {
  const name = email.from?.[0]?.name ?? email.from?.[0]?.email ?? "?";
  const parts = name.replace(/[<>"]/g, "").trim().split(/[\s.@]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}

function sender(email: Email): string {
  return email.from?.[0]?.name || email.from?.[0]?.email || "(unknown sender)";
}

/** Today → time; yesterday → "Yesterday"; else short date. */
function when(iso: string, hour24: boolean): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return formatTime(d, hour24);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

/**
 * Collapses a mailbox into conversations.
 *
 * Stalwart already assigns `threadId`, so this is grouping rather than
 * inference. The row shown for a thread is its newest message — which is the
 * one whose subject, sender and date you want when deciding whether to open it
 * — and the thread counts as unread if ANY message in it is, so a reply cannot
 * hide behind an older message you have already read.
 *
 * Ordering is by the newest message in each thread, so a conversation that gets
 * a reply returns to the top the way it does in every other mail client.
 */
export interface Conversation {
  /** The newest message: what the row renders. */
  head: Email;
  /** Newest first, head included. */
  messages: Email[];
  unread: boolean;
  flagged: boolean;
  hasAttachment: boolean;
}

export function groupIntoThreads(emails: Email[]): Conversation[] {
  const byThread = new Map<string, Email[]>();
  for (const email of emails) {
    // Fall back to the message id: a server that omits threadId should give one
    // conversation per message, not one giant conversation of everything.
    const key = email.threadId || email.id;
    const bucket = byThread.get(key);
    if (bucket) bucket.push(email);
    else byThread.set(key, [email]);
  }

  // receivedAt has one-second resolution, so messages delivered in the same
  // second tie — common for anything sent in a batch. Falling back to the id
  // keeps the order stable instead of arbitrary, which matters because the
  // "newest" message is the one the row renders and the reader expands.
  const newestFirst = (a: Email, b: Email) =>
    b.receivedAt.localeCompare(a.receivedAt) || b.id.localeCompare(a.id);

  return [...byThread.values()]
    .map((messages) => {
      const sorted = [...messages].sort(newestFirst);
      return {
        head: sorted[0],
        messages: sorted,
        unread: sorted.some((e) => !e.keywords["$seen"]),
        flagged: sorted.some((e) => e.keywords["$flagged"]),
        hasAttachment: sorted.some((e) => e.hasAttachment),
      };
    })
    .sort((a, b) => newestFirst(a.head, b.head));
}

export function MessageList({
  title, conversations, density, previewLines, senderImages, hour24, selectedId, cursorId, checked, loading, error, searching,
  onOpen, onRefresh, onToggleFlag, onToggleCheck, onCheckAll,
  onBulkArchive, onBulkDelete, onBulkRead,
}: {
  title: string;
  conversations: Conversation[];
  density: "comfortable" | "compact";
  previewLines: 0 | 1 | 2 | 3;
  senderImages: boolean;
  hour24: boolean;
  /** Head id of the open conversation. */
  selectedId: string | null;
  cursorId: string | null;
  /** Head ids of checked conversations. */
  checked: Set<string>;
  loading: boolean;
  error: string | null;
  searching: boolean;
  onOpen: (c: Conversation) => void;
  onRefresh: () => void;
  onToggleFlag: (c: Conversation) => void;
  onToggleCheck: (c: Conversation) => void;
  onCheckAll: (on: boolean) => void;
  onBulkArchive: () => void;
  onBulkDelete: () => void;
  onBulkRead: () => void;
}) {
  const allChecked = conversations.length > 0 && checked.size === conversations.length;

  return (
    <section className="list" data-screen-label="Message list">
      <div className="list-head">
        <h2>
          {title} <span className="n">{loading ? "" : conversations.length}</span>
        </h2>
        <div className="list-tools">
          {/* The bulk bar replaces the refresh control rather than sitting
              beside it: with a selection live, refreshing would drop it. */}
          {checked.size > 0 ? (
            <>
              <span style={{ font: "500 12px/1 Inter", color: "var(--tx3)", marginRight: 4 }}>
                {checked.size} selected
              </span>
              <button className="ibtn" title="Mark read" onClick={onBulkRead}><Icon name="mail-open" /></button>
              <button className="ibtn" title="Archive" onClick={onBulkArchive}><Icon name="archive" /></button>
              <button className="ibtn" title="Delete" onClick={onBulkDelete}><Icon name="trash-2" /></button>
              <button className="ibtn" title="Clear selection" onClick={() => onCheckAll(false)}><Icon name="x" /></button>
            </>
          ) : (
            <>
              <button
                className="ibtn"
                title="Select all"
                onClick={() => onCheckAll(true)}
                disabled={conversations.length === 0}
              >
                <Icon name="check" />
              </button>
              <button className="ibtn" title="Refresh" onClick={onRefresh}>
                <Icon name="refresh-cw" />
              </button>
            </>
          )}
        </div>
      </div>

      <div
        className={`rows${density === "compact" ? " compact" : ""}${senderImages ? "" : " no-avatars"}`}
        data-preview={previewLines}
      >
        {error && (
          <div className="empty" role="alert">
            <Icon name="triangle-alert" />
            <p>{error}</p>
          </div>
        )}

        {!error && loading && (
          <div className="empty">
            <Icon name="loader" />
            <p>Loading…</p>
          </div>
        )}

        {!error && !loading && conversations.length === 0 && (
          <div className="empty">
            <Icon name="sparkles" />
            <p>{searching ? "Nothing matches that search." : "Nothing here. Enjoy the quiet."}</p>
          </div>
        )}

        {!error && !loading &&
          conversations.map((conversation) => {
            const email = conversation.head;
            const isChecked = checked.has(email.id);
            // Everyone who wrote in the thread, newest first, de-duplicated —
            // "Abed, Laith" tells you more about a conversation than the last
            // sender repeated.
            const who = [...new Set(conversation.messages.map(sender))].reverse().join(", ");
            return (
              <div
                key={email.id}
                className={[
                  "row",
                  conversation.unread ? "unread" : "",
                  selectedId === email.id ? "open" : "",
                  isChecked ? "sel" : "",
                  cursorId === email.id ? "cursor" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => onOpen(conversation)}
                role="button"
                tabIndex={0}
                aria-selected={selectedId === email.id}
                onKeyDown={(e) => { if (e.key === "Enter") onOpen(conversation); }}
              >
                <button
                  className={`ck${isChecked ? " on" : ""}`}
                  onClick={(e) => { e.stopPropagation(); onToggleCheck(conversation); }}
                  aria-pressed={isChecked}
                  title={isChecked ? "Deselect" : "Select"}
                >
                  <Icon name="check" />
                </button>
                <div className="av" style={{ background: colourFor(email.from?.[0]?.email ?? who) }}>
                  {initials(email)}
                </div>
                <div className="bd">
                  <div className="l1">
                    <span className="who">
                      {who}
                      {conversation.messages.length > 1 && (
                        <span style={{ color: "var(--tx3)", fontWeight: 400 }}>
                          {" "}({conversation.messages.length})
                        </span>
                      )}
                    </span>
                    <span className="when">{when(email.receivedAt, hour24)}</span>
                  </div>
                  <div className="sj">{email.subject || "(no subject)"}</div>
                  <div className="sn">{email.preview}</div>
                  {conversation.hasAttachment && (
                    <div className="meta">
                      <Icon name="paperclip" className="clip" />
                    </div>
                  )}
                </div>
                <button
                  className={`star${conversation.flagged ? " on" : ""}`}
                  title={conversation.flagged ? "Unstar" : "Star"}
                  aria-pressed={conversation.flagged}
                  onClick={(e) => { e.stopPropagation(); onToggleFlag(conversation); }}
                >
                  <Icon name="star" />
                </button>
              </div>
            );
          })}
      </div>

      {/* Select-all is in the toolbar above; this keeps its state announced. */}
      <span className="sr-only" aria-live="polite">
        {allChecked ? "All messages selected" : `${checked.size} messages selected`}
      </span>
    </section>
  );
}
